<template>
  <div
    v-for="position in POSITIONS"
    :key="position"
    class="toast-custom"
    :class="`toast-custom--${position}`"
  >
    <TransitionGroup name="toast" tag="div" class="toast-custom__list">
      <toast-item
        v-for="item of groupedToasts[position]"
        :key="item.id"
        :title="item.title"
        :type="item.type"
        :duration="item.duration"
        @close-toast="toast.remove(item.id)"
      />
    </TransitionGroup>
  </div>
</template>

<script setup>
import ToastItem from "@/components/ToastItem.vue";
import { computed } from "vue";
import { useToast } from "@/composables/useToast";

const POSITIONS = ["top-right", "top-center"];
const DEFAULT_POSITION = "top-right";

const toast = useToast();

const groupedToasts = computed(() => {
  const groups = Object.fromEntries(
    POSITIONS.map((position) => [position, []]),
  );
  for (const item of toast.toasts.value) {
    const position = item.position || DEFAULT_POSITION;
    (groups[position] ??= []).push(item);
  }
  return groups;
});
</script>

<style lang="scss" scoped>
.toast-custom {
  --toast-ease-in-out: cubic-bezier(0.4, 0, 0.2, 1);

  position: fixed;
  top: 20px;
  z-index: 1100;

  &--top-right {
    right: 20px;
    justify-items: end;
  }

  &--top-center {
    left: 50%;
    transform: translateX(-50%);
    max-width: calc(100vw - 40px);
    justify-items: center;
  }

  &__list {
    display: grid;
    justify-items: inherit;
  }

  &__row {
    padding-bottom: 8px;
    transform-origin: top center;
  }
}

.toast-enter-active {
  transition:
    grid-template-rows 0.3s var(--toast-ease-in-out),
    padding-bottom 0.3s var(--toast-ease-in-out),
    opacity 0.3s cubic-bezier(0.4, 0, 1, 1),
    transform 0.3s var(--toast-ease-in-out);
}

.toast-leave-active {
  transition:
    grid-template-rows 0.3s var(--toast-ease-in-out),
    padding-bottom 0.3s var(--toast-ease-in-out),
    opacity 0.3s cubic-bezier(0, 0, 0.2, 1),
    transform 0.3s var(--toast-ease-in-out);
}

.toast-enter-from,
.toast-leave-to {
  grid-template-rows: 0fr;
  padding-bottom: 0;
  opacity: 0;
  transform: scale(0.85);
}

@media (prefers-reduced-motion: reduce) {
  .toast-enter-active,
  .toast-leave-active {
    transition: none;
  }
}

@media (max-width: 768px) {
  .toast-custom {
    top: 15px;
  }

  .toast-custom--top-right {
    margin-left: 20px;
  }
}
</style>
